import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import AppLayout from "components/ui/AppLayout";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import DeleteConfirmModal from "components/ui/DeleteConfirmModal";
import { contactsApi } from "api/contacts";
import { dealsApi } from "api/deals";
import { tasksApi } from "api/tasks";
import { callsApi } from "api/calls";
import { invoicesApi } from "api/invoices";
import { useToast } from "components/ui/Toast";

const CONSENT_CONFIG = {
  granted: { bg: "#DCFCE7", color: "#15803D", label: "Consent Granted" },
  denied: { bg: "#FEE2E2", color: "#B91C1C", label: "Consent Denied" },
  unknown: { bg: "#F3F4F6", color: "#4B5563", label: "Consent Unknown" },
};

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

function formatMoney(value) {
  if (value >= 10000000) return `₹${(value / 10000000).toFixed(1)}Cr`;
  if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
  return `₹${Number(value).toLocaleString("en-IN")}`;
}

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [contact, setContact] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [deals, setDeals] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [calls, setCalls] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const load = async () => {
    setIsLoading(true);
    setLoadError("");
    try {
      const [contactData, timelineData] = await Promise.all([
        contactsApi.get(id),
        contactsApi.timeline(id).catch(() => []),
      ]);
      setContact(contactData);
      setTimeline(Array.isArray(timelineData) ? timelineData : timelineData?.items || []);
      const [dealsData, tasksData, callsData, invoicesData] = await Promise.all([
        dealsApi.getAll("", { contactId: id }).catch(() => []),
        tasksApi.getAll("", { linkedType: "contact", linkedId: id }).catch(() => []),
        callsApi.listByContact(id).catch(() => []),
        invoicesApi.getAll({ contactId: id }).catch(() => []),
      ]);
      setDeals(Array.isArray(dealsData) ? dealsData : []);
      setTasks(Array.isArray(tasksData) ? tasksData : []);
      setCalls(Array.isArray(callsData) ? callsData : []);
      setInvoices(Array.isArray(invoicesData) ? invoicesData : []);
    } catch (error) {
      setLoadError(error?.message || "Unable to load customer data.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const handleDelete = async () => {
    try {
      await contactsApi.remove(id);
      toast.success("Contact deleted.");
      navigate("/contacts");
    } catch (error) {
      toast.error(error?.message || "Could not delete the contact.");
    }
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div className="px-4 md:px-6 lg:px-8 py-6 max-w-screen-2xl mx-auto">
          <p className="font-caption text-sm" style={{ color: "var(--color-text-secondary)" }}>Loading customer...</p>
        </div>
      </AppLayout>
    );
  }

  if (loadError || !contact) {
    return (
      <AppLayout>
        <div className="px-4 md:px-6 lg:px-8 py-6 max-w-screen-2xl mx-auto">
          <div className="rounded-xl border px-4 py-10 text-center" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
            <Icon name="AlertTriangle" size={24} color="var(--color-error)" className="mx-auto mb-2" />
            <p className="font-caption text-sm mb-4" style={{ color: "var(--color-text-secondary)" }}>
              {loadError || "Customer not found."}
            </p>
            <Button variant="outline" size="sm" iconName="ArrowLeft" iconPosition="left" onClick={() => navigate("/contacts")}>
              Back to Contacts
            </Button>
          </div>
        </div>
      </AppLayout>
    );
  }

  const consentCfg = CONSENT_CONFIG[contact.whatsappConsent] || CONSENT_CONFIG.unknown;
  const initials = (contact.fullName || contact.name || "?").split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();

  return (
    <AppLayout>
      <div className="px-4 md:px-6 lg:px-8 py-6 max-w-screen-2xl mx-auto">
        {/* Back */}
        <button
          onClick={() => navigate("/contacts")}
          className="flex items-center gap-1.5 font-caption text-sm mb-4 hover:underline"
          style={{ color: "var(--color-primary)" }}
        >
          <Icon name="ArrowLeft" size={14} color="currentColor" />
          Back to Contacts
        </button>

        {/* Header */}
        <div className="rounded-xl border p-6 mb-6" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
          <div className="flex flex-col sm:flex-row gap-5">
            <div
              className="flex items-center justify-center rounded-full font-bold text-lg flex-shrink-0"
              style={{ width: 64, height: 64, background: "var(--color-primary)", color: "#fff" }}
            >
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h1 className="font-heading font-bold text-2xl" style={{ color: "var(--color-foreground)" }}>
                    {contact.fullName || contact.name}
                  </h1>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <span className="px-2.5 py-1 rounded-full font-caption text-xs font-semibold" style={{ background: consentCfg.bg, color: consentCfg.color }}>
                      {consentCfg.label}
                    </span>
                    {contact.convertedToDealId ? (
                      <span className="px-2.5 py-1 rounded-full font-caption text-xs font-semibold" style={{ background: "#D1FAE5", color: "#065F46" }}>
                        Converted to Deal
                      </span>
                    ) : (
                      <span className="px-2.5 py-1 rounded-full font-caption text-xs font-semibold" style={{ background: "#F3F4F6", color: "#4B5563" }}>
                        Open
                      </span>
                    )}
                    <span className="font-caption text-xs" style={{ color: "var(--color-text-secondary)" }}>
                      Source: {contact.source || "—"}
                    </span>
                  </div>
                </div>
                <Button variant="destructive" size="sm" iconName="Trash2" iconPosition="left" onClick={() => setIsDeleteOpen(true)}>
                  Delete
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                <div className="flex items-center gap-2">
                  <Icon name="Mail" size={14} color="var(--color-text-secondary)" />
                  <span className="font-caption text-sm truncate" style={{ color: "var(--color-foreground)" }}>{contact.email || "—"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="Phone" size={14} color="var(--color-text-secondary)" />
                  <span className="font-caption text-sm" style={{ color: "var(--color-foreground)" }}>{contact.phone || "—"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="Tag" size={14} color="var(--color-text-secondary)" />
                  <span className="font-caption text-sm" style={{ color: "var(--color-foreground)" }}>
                    {Array.isArray(contact.tags) && contact.tags.length ? contact.tags.join(", ") : "—"}
                  </span>
                </div>
              </div>
              {contact.companyId && (
                <p className="font-caption text-sm mt-2" style={{ color: "var(--color-text-secondary)" }}>
                  Company: {contact.companyId}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="rounded-xl border p-4" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
            <p className="font-caption text-xs uppercase tracking-wide" style={{ color: "var(--color-text-secondary)" }}>Deals</p>
            <p className="font-heading font-bold text-xl mt-1" style={{ color: "var(--color-foreground)" }}>{deals.length}</p>
            <p className="font-caption text-xs mt-1" style={{ color: "var(--color-text-secondary)" }}>{formatMoney(deals.reduce((s, d) => s + (d.amount || 0), 0))} total</p>
          </div>
          <div className="rounded-xl border p-4" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
            <p className="font-caption text-xs uppercase tracking-wide" style={{ color: "var(--color-text-secondary)" }}>Tasks</p>
            <p className="font-heading font-bold text-xl mt-1" style={{ color: "var(--color-foreground)" }}>{tasks.length}</p>
            <p className="font-caption text-xs mt-1" style={{ color: "var(--color-text-secondary)" }}>{tasks.filter((t) => t.status === "open").length} open</p>
          </div>
          <div className="rounded-xl border p-4" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
            <p className="font-caption text-xs uppercase tracking-wide" style={{ color: "var(--color-text-secondary)" }}>Calls</p>
            <p className="font-heading font-bold text-xl mt-1" style={{ color: "var(--color-foreground)" }}>{calls.length}</p>
          </div>
          <div className="rounded-xl border p-4" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
            <p className="font-caption text-xs uppercase tracking-wide" style={{ color: "var(--color-text-secondary)" }}>Invoices</p>
            <p className="font-heading font-bold text-xl mt-1" style={{ color: "var(--color-foreground)" }}>{invoices.length}</p>
            <p className="font-caption text-xs mt-1" style={{ color: "var(--color-text-secondary)" }}>{formatMoney(invoices.reduce((s, inv) => s + (inv.amountIncTax || 0), 0))} total</p>
          </div>
        </div>

        {/* Deals */}
        <section className="rounded-xl border mb-6 overflow-hidden" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
          <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: "1px solid var(--color-border)" }}>
            <h2 className="font-heading font-semibold text-base" style={{ color: "var(--color-foreground)" }}>Deals</h2>
            <Link to="/opportunities" className="font-caption text-sm hover:underline" style={{ color: "var(--color-primary)" }}>View all →</Link>
          </div>
          {deals.length === 0 ? (
            <p className="font-caption text-sm px-4 py-8 text-center" style={{ color: "var(--color-text-secondary)" }}>No deals for this customer yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" style={{ minWidth: 600 }}>
                <thead>
                  <tr style={{ background: "var(--color-muted)" }}>
                    <th className="px-4 py-2 text-left font-caption text-xs uppercase" style={{ color: "var(--color-text-secondary)" }}>Deal</th>
                    <th className="px-4 py-2 text-left font-caption text-xs uppercase" style={{ color: "var(--color-text-secondary)" }}>Stage</th>
                    <th className="px-4 py-2 text-right font-caption text-xs uppercase" style={{ color: "var(--color-text-secondary)" }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {deals.map((d) => (
                    <tr key={d.id} style={{ borderTop: "1px solid var(--color-border)" }}>
                      <td className="px-4 py-2.5 font-caption text-sm" style={{ color: "var(--color-foreground)" }}>{d.name}</td>
                      <td className="px-4 py-2.5 font-caption text-sm" style={{ color: "var(--color-text-secondary)" }}>{d.stage}</td>
                      <td className="px-4 py-2.5 font-caption text-sm text-right font-semibold" style={{ color: "var(--color-foreground)" }}>{formatMoney(d.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Tasks */}
        <section className="rounded-xl border mb-6 overflow-hidden" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
          <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: "1px solid var(--color-border)" }}>
            <h2 className="font-heading font-semibold text-base" style={{ color: "var(--color-foreground)" }}>Tasks</h2>
            <Link to="/tasks" className="font-caption text-sm hover:underline" style={{ color: "var(--color-primary)" }}>View all →</Link>
          </div>
          {tasks.length === 0 ? (
            <p className="font-caption text-sm px-4 py-8 text-center" style={{ color: "var(--color-text-secondary)" }}>No tasks for this customer.</p>
          ) : (
            <div className="divide-y" style={{ borderColor: "var(--color-border)" }}>
              {tasks.map((t) => (
                <div key={t.id} className="px-4 py-2.5 flex items-center justify-between gap-2">
                  <span className="font-caption text-sm truncate" style={{ color: t.status === "done" ? "var(--color-text-secondary)" : "var(--color-foreground)", textDecoration: t.status === "done" ? "line-through" : "none" }}>{t.title}</span>
                  <span className="font-caption text-xs px-2 py-0.5 rounded-full flex-shrink-0" style={{ background: t.status === "done" ? "#DCFCE7" : "#DBEAFE", color: t.status === "done" ? "#065F46" : "#1D4ED8" }}>{t.status}</span>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Calls */}
        <section className="rounded-xl border mb-6 overflow-hidden" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
          <div className="px-4 py-3" style={{ borderBottom: "1px solid var(--color-border)" }}>
            <h2 className="font-heading font-semibold text-base" style={{ color: "var(--color-foreground)" }}>Calls</h2>
          </div>
          {calls.length === 0 ? (
            <p className="font-caption text-sm px-4 py-8 text-center" style={{ color: "var(--color-text-secondary)" }}>No calls for this customer.</p>
          ) : (
            <div className="divide-y" style={{ borderColor: "var(--color-border)" }}>
              {calls.map((c) => (
                <div key={c.id || c.callId} className="px-4 py-2.5 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Icon name={c.direction === "inbound" ? "PhoneIncoming" : "PhoneOutgoing"} size={14} color="var(--color-text-secondary)" />
                    <span className="font-caption text-sm" style={{ color: "var(--color-foreground)" }}>{c.direction} · {c.phone}</span>
                  </div>
                  <span className="font-caption text-xs" style={{ color: "var(--color-text-secondary)" }}>{formatDate(c.createdAtUtc || c.createdAt)}</span>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Invoices */}
        <section className="rounded-xl border mb-6 overflow-hidden" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
          <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: "1px solid var(--color-border)" }}>
            <h2 className="font-heading font-semibold text-base" style={{ color: "var(--color-foreground)" }}>Invoices</h2>
            <Link to="/invoices" className="font-caption text-sm hover:underline" style={{ color: "var(--color-primary)" }}>View all →</Link>
          </div>
          {invoices.length === 0 ? (
            <p className="font-caption text-sm px-4 py-8 text-center" style={{ color: "var(--color-text-secondary)" }}>No invoices for this customer.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" style={{ minWidth: 500 }}>
                <thead>
                  <tr style={{ background: "var(--color-muted)" }}>
                    <th className="px-4 py-2 text-left font-caption text-xs uppercase" style={{ color: "var(--color-text-secondary)" }}>Number</th>
                    <th className="px-4 py-2 text-right font-caption text-xs uppercase" style={{ color: "var(--color-text-secondary)" }}>Amount</th>
                    <th className="px-4 py-2 text-left font-caption text-xs uppercase" style={{ color: "var(--color-text-secondary)" }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.id} style={{ borderTop: "1px solid var(--color-border)" }}>
                      <td className="px-4 py-2.5 font-caption text-sm font-medium" style={{ color: "var(--color-foreground)" }}>{inv.invoiceNumber}</td>
                      <td className="px-4 py-2.5 font-caption text-sm text-right font-semibold" style={{ color: "var(--color-foreground)" }}>{formatMoney(inv.amountIncTax)}</td>
                      <td className="px-4 py-2.5"><span className="px-2 py-0.5 rounded-full font-caption text-xs font-semibold" style={{ background: inv.status === "paid" ? "#DCFCE7" : inv.status === "sent" ? "#DBEAFE" : "#F3F4F6", color: inv.status === "paid" ? "#065F46" : inv.status === "sent" ? "#1D4ED8" : "#4B5563" }}>{inv.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Timeline */}
        <section className="rounded-xl border overflow-hidden" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
          <div className="px-4 py-3" style={{ borderBottom: "1px solid var(--color-border)" }}>
            <h2 className="font-heading font-semibold text-base" style={{ color: "var(--color-foreground)" }}>Timeline</h2>
          </div>
          {timeline.length === 0 ? (
            <p className="font-caption text-sm px-4 py-8 text-center" style={{ color: "var(--color-text-secondary)" }}>No timeline events yet.</p>
          ) : (
            <div className="divide-y px-4" style={{ borderColor: "var(--color-border)" }}>
              {timeline.map((entry, idx) => (
                <div key={idx} className="py-3 flex gap-3">
                  <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ background: "var(--color-primary)" }} />
                  <div className="min-w-0">
                    <p className="font-caption text-sm" style={{ color: "var(--color-foreground)" }}>{entry.action || entry.type || JSON.stringify(entry)}</p>
                    <p className="font-caption text-xs mt-0.5" style={{ color: "var(--color-text-secondary)" }}>{formatDate(entry.atUtc || entry.createdAtUtc || entry.timestamp)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <DeleteConfirmModal
          isOpen={isDeleteOpen}
          itemName={contact.fullName || contact.name || "Contact"}
          onClose={() => setIsDeleteOpen(false)}
          onConfirm={handleDelete}
        />
      </div>
    </AppLayout>
  );
}